"use client";

import { formatBlogDate } from "@/components/blog";
import type { BlogPost } from "@/lib/blog";
import {
  type AiLanguagePracticePage,
  aiLanguagePracticePages,
  getAiTutorPath,
  getSpeakingPracticePath,
} from "@/lib/seo/aiLanguagePracticePages";
import { toLanguageArchiveSlug } from "@/lib/seo/blogArchiveUtils";
import {
  handwrittenAdultLearningBlogSlugs,
  handwrittenBetweenLessonBlogSlugs,
  handwrittenExamPrepBlogSlugs,
} from "@/lib/seo/blogArticleSeo";
import { resolveBlogPostLanguage } from "@/lib/seo/blogPostLanguage";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  Languages,
  Layers3,
  MessageCircle,
  Mic2,
  Search,
  Sparkles,
} from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";

interface BlogPageClientProps {
  blogPosts: BlogPost[];
}

type FilterType = "category" | "language";

type VisualTone = {
  bg: string;
  line: string;
  mark: string;
  text: string;
};

const featuredPracticeLanguageNames = [
  "Spanish",
  "English",
  "French",
  "Japanese",
  "German",
  "Korean",
];

const seoHubLinks = [
  {
    href: "/ai",
    label: "AI tutor hub",
    description: "Find the canonical AI tutor pages for popular languages.",
  },
  {
    href: "/practice",
    label: "Speaking practice hub",
    description: "Browse language-specific speaking practice pages.",
  },
  {
    href: "/tools/writing-practice-language-learning",
    label: "Writing practice tool",
    description: "Use writing drills before turning answers into spoken practice.",
  },
  {
    href: "/tools/fill-in-the-gap-language-practice",
    label: "Fill-in-the-gap tool",
    description: "Practice grammar and vocabulary with language-specific gap exercises.",
  },
];

const visualTones: VisualTone[] = [
  {
    bg: "bg-[#fff5e8]",
    line: "bg-orange-200/80",
    mark: "bg-orange-500",
    text: "text-orange-950",
  },
  {
    bg: "bg-[#edf7f4]",
    line: "bg-emerald-200/80",
    mark: "bg-emerald-600",
    text: "text-emerald-950",
  },
  {
    bg: "bg-[#f1f0ff]",
    line: "bg-indigo-200/80",
    mark: "bg-indigo-600",
    text: "text-indigo-950",
  },
  {
    bg: "bg-[#fff0f3]",
    line: "bg-rose-200/80",
    mark: "bg-rose-500",
    text: "text-rose-950",
  },
];

function toCategoryLabel(category: string): string {
  return category
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function normalizeSearchValue(value: string): string {
  return value.trim().toLowerCase();
}

function getReadingTime(post: BlogPost): number {
  const wordCount = post.content.split(/\s+/).filter(Boolean).length;
  return Math.max(2, Math.ceil(wordCount / 220));
}

function getTone(post: BlogPost, index: number): VisualTone {
  const seed = post.slug.split("").reduce((total, char) => total + char.charCodeAt(0), index);
  return visualTones[seed % visualTones.length];
}

function getFeaturedPracticePages(): AiLanguagePracticePage[] {
  return featuredPracticeLanguageNames
    .map((name) => aiLanguagePracticePages.find((page) => page.name === name))
    .filter((page): page is AiLanguagePracticePage => Boolean(page));
}

function isComparisonPost(post: BlogPost): boolean {
  const haystack = `${post.slug} ${post.title}`.toLowerCase();
  return (
    haystack.includes("alternative") ||
    haystack.includes(" vs ") ||
    haystack.startsWith("vs-") ||
    haystack.includes("best ") ||
    haystack.includes("apps")
  );
}

function FilterButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex min-h-10 items-center justify-center rounded-full px-4 text-sm font-semibold transition-colors ${
        active
          ? "bg-gray-950 text-white shadow-sm"
          : "border border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:text-gray-950"
      }`}
    >
      {children}
    </button>
  );
}

function PostVisual({
  post,
  index,
  large = false,
}: {
  post: BlogPost;
  index: number;
  large?: boolean;
}) {
  const tone = getTone(post, index);
  const displayLanguage = resolveBlogPostLanguage(post);

  return (
    <div
      className={`relative overflow-hidden rounded-lg ${tone.bg} ${
        large ? "min-h-[270px]" : "h-44"
      }`}
      aria-hidden="true"
    >
      <div className="absolute left-5 top-5 flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${tone.mark}`} />
        <span className={`text-xs font-bold uppercase tracking-[0.2em] ${tone.text}`}>
          {displayLanguage}
        </span>
      </div>
      <div className="absolute inset-x-5 bottom-5 space-y-3">
        <div className={`h-2 w-20 rounded-full ${tone.line}`} />
        <div className={`h-2 w-full rounded-full ${tone.line}`} />
        <div className={`h-2 w-2/3 rounded-full ${tone.line}`} />
      </div>
      <div className="absolute -right-7 -top-7 h-28 w-28 rounded-full bg-white/70" />
      <div className="absolute bottom-7 right-7 grid h-20 w-20 place-items-center rounded-full bg-white/80 shadow-sm">
        <BookOpen className={`h-8 w-8 ${tone.text}`} />
      </div>
    </div>
  );
}

function MetaRow({ post }: { post: BlogPost }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-gray-500">
      <span className="inline-flex items-center gap-1.5">
        <CalendarDays className="h-3.5 w-3.5" />
        {formatBlogDate(post.date)}
      </span>
      <span>{getReadingTime(post)} min read</span>
    </div>
  );
}

function FeaturedArticle({ post }: { post: BlogPost }) {
  const displayLanguage = resolveBlogPostLanguage(post);

  return (
    <article className="grid overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm lg:grid-cols-[1.05fr_0.95fr]">
      <Link href={`/blog/${post.slug}`} className="block">
        <PostVisual post={post} index={0} large />
      </Link>
      <div className="flex flex-col p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            href={`/blog/category/${post.category}`}
            className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-orange-700 transition-colors hover:bg-orange-100"
          >
            {toCategoryLabel(post.category)}
          </Link>
          <Link
            href={`/blog/language/${toLanguageArchiveSlug(displayLanguage)}`}
            className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-gray-700 transition-colors hover:bg-gray-200"
          >
            {displayLanguage}
          </Link>
        </div>
        <h2 className="mt-5 font-serif text-3xl font-semibold leading-tight tracking-tight text-gray-950 sm:text-4xl">
          <Link href={`/blog/${post.slug}`} className="hover:text-orange-700">
            {post.title}
          </Link>
        </h2>
        <p className="mt-4 text-base leading-7 text-gray-600">{post.description}</p>
        <div className="mt-6">
          <MetaRow post={post} />
        </div>
        <Link
          href={`/blog/${post.slug}`}
          className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-gray-950 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-600"
        >
          Read the guide
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}

function ArticleCard({ post, index }: { post: BlogPost; index: number }) {
  const displayLanguage = resolveBlogPostLanguage(post);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="group"
    >
      <Link
        href={`/blog/${post.slug}`}
        className="grid h-full gap-4 rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md"
      >
        <PostVisual post={post} index={index + 1} />
        <div className="flex min-h-[250px] flex-col px-1 pb-1">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">
            <span>{toCategoryLabel(post.category)}</span>
            <span className="h-1 w-1 rounded-full bg-gray-300" />
            <span>{displayLanguage}</span>
          </div>
          <h3 className="mt-3 text-xl font-semibold leading-snug text-gray-950 group-hover:text-orange-700">
            {post.title}
          </h3>
          <p className="mt-3 line-clamp-3 text-sm leading-6 text-gray-600">{post.description}</p>
          <div className="mt-auto pt-5">
            <MetaRow post={post} />
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-700">
              Read article
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

function ArchiveLinks({ categories, languages }: { categories: string[]; languages: string[] }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-full bg-orange-50 text-orange-700">
          <Layers3 className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-950">Browse the archive</h2>
          <p className="text-sm text-gray-500">Jump straight to the topic or language you need.</p>
        </div>
      </div>
      <div className="mt-5 space-y-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-400">Topics</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {categories.map((category) => (
              <Link
                key={category}
                href={`/blog/category/${category}`}
                className="rounded-full border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-orange-300 hover:text-orange-700"
              >
                {toCategoryLabel(category)}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-400">Languages</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {languages.map((language) => (
              <Link
                key={language}
                href={`/blog/language/${toLanguageArchiveSlug(language)}`}
                className="rounded-full border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-orange-300 hover:text-orange-700"
              >
                {language}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PracticeRail() {
  const practicePages = aiLanguagePracticePages.slice(0, 6);

  return (
    <aside className="rounded-lg border border-orange-200 bg-[#fff7ed] p-5 shadow-sm lg:sticky lg:top-6">
      <div className="grid h-11 w-11 place-items-center rounded-full bg-orange-500 text-white shadow-sm">
        <Mic2 className="h-5 w-5" />
      </div>
      <h2 className="mt-4 text-lg font-semibold text-gray-950">Turn a guide into practice</h2>
      <p className="mt-2 text-sm leading-6 text-gray-600">
        Pick a language, then speak through the examples with ChickyTutor.
      </p>
      <div className="mt-5 space-y-3">
        {practicePages.map((page) => (
          <div key={page.slug} className="rounded-lg bg-white/80 p-3">
            <p className="text-sm font-semibold text-gray-950">{page.name}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Link
                href={getSpeakingPracticePath(page)}
                className="text-xs font-semibold text-orange-700 hover:text-orange-800"
              >
                Speaking
              </Link>
              <Link
                href={getAiTutorPath(page)}
                className="text-xs font-semibold text-gray-600 hover:text-gray-950"
              >
                AI tutor
              </Link>
            </div>
          </div>
        ))}
      </div>
      <Link
        href="/tools/writing-practice-language-learning"
        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-orange-800 hover:text-orange-900"
      >
        Writing practice tool
        <ArrowRight className="h-4 w-4" />
      </Link>
    </aside>
  );
}

function SeoGrowthLinks({ posts }: { posts: BlogPost[] }) {
  const practicePages = getFeaturedPracticePages();
  const comparisonPosts = posts.filter(isComparisonPost).slice(0, 6);

  return (
    <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-6 lg:px-8">
      <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
                Practice by language
              </p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-gray-950">
                Jump from reading to speaking.
              </h2>
            </div>
            <Link
              href="/blog/language-combo-library"
              className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-orange-700 hover:text-orange-800"
            >
              Language combo library
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {practicePages.map((page) => (
              <div key={page.slug} className="rounded-lg border border-gray-200 bg-[#fbfaf7] p-4">
                <p className="font-semibold text-gray-950">{page.name}</p>
                <p className="mt-1 line-clamp-2 text-sm leading-6 text-gray-600">
                  {page.learnerIntent}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    href={getSpeakingPracticePath(page)}
                    className="rounded-full bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600"
                  >
                    {page.name} speaking
                  </Link>
                  <Link
                    href={getAiTutorPath(page)}
                    className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-orange-300 hover:text-orange-700"
                  >
                    AI tutor
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {seoHubLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-orange-300 hover:bg-orange-50/40"
              >
                <span className="text-sm font-semibold text-gray-950">{link.label}</span>
                <span className="mt-1 block text-xs leading-5 text-gray-500">
                  {link.description}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
            Popular comparisons
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-gray-950">
            Compare before you commit.
          </h2>
          <div className="mt-5 space-y-3">
            {comparisonPosts.map((post) => (
              <Link
                key={post.slug}
                href={`/blog/${post.slug}`}
                className="group block rounded-lg border border-gray-200 p-4 transition-colors hover:border-orange-300 hover:bg-orange-50/40"
              >
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400">
                  {toCategoryLabel(post.category)}
                </span>
                <span className="mt-2 block font-semibold leading-snug text-gray-950 group-hover:text-orange-700">
                  {post.title}
                </span>
                <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-700">
                  Read comparison
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HandwrittenClusterLinks({ posts }: { posts: BlogPost[] }) {
  const postsBySlug = new Map(posts.map((post) => [post.slug, post]));
  const examPosts = handwrittenExamPrepBlogSlugs
    .map((slug) => postsBySlug.get(slug))
    .filter((post): post is BlogPost => Boolean(post));
  const adultPosts = handwrittenAdultLearningBlogSlugs
    .map((slug) => postsBySlug.get(slug))
    .filter((post): post is BlogPost => Boolean(post));
  const betweenLessonPosts = handwrittenBetweenLessonBlogSlugs
    .map((slug) => postsBySlug.get(slug))
    .filter((post): post is BlogPost => Boolean(post));

  if (examPosts.length === 0 && adultPosts.length === 0 && betweenLessonPosts.length === 0) {
    return null;
  }

  const groups = [
    {
      label: "Exam and integration speaking",
      description:
        "Dutch inburgering, NT2, CEFR, Goethe, DELF, DELE, IELTS, Cambridge, and IB oral practice.",
      posts: examPosts,
    },
    {
      label: "Adult learning routines",
      description:
        "Speaking plans for busy adults, shy learners, workplace needs, solo practice, and learning after 40.",
      posts: adultPosts,
    },
    {
      label: "Between lessons: Europe",
      description:
        "French, Dutch, German, Spanish, Italian, UK English, European Portuguese, and Polish.",
      posts: betweenLessonPosts.slice(0, 8),
    },
    {
      label: "Between lessons: global",
      description:
        "Japanese, Korean, Mandarin, Hindi, Egyptian Arabic, spoken Tamil, Brazilian Portuguese, and Cantonese.",
      posts: betweenLessonPosts.slice(8, 16),
    },
    {
      label: "Between lessons: regional",
      description:
        "Regional English, Spanish, French, and Arabic guides built around distinct classroom and family situations.",
      posts: betweenLessonPosts.slice(16),
    },
  ];

  return (
    <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
              Focused speaking guides
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-gray-950">
              Handwritten speaking guides, grouped for faster browsing.
            </h2>
          </div>
          <a
            href="#latest-guides"
            className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-orange-700 hover:text-orange-800"
          >
            All blog posts
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          {groups.map((group) => (
            <div key={group.label} className="rounded-lg border border-gray-200 bg-[#fbfaf7] p-4">
              <h3 className="text-lg font-semibold text-gray-950">{group.label}</h3>
              <p className="mt-2 text-sm leading-6 text-gray-600">{group.description}</p>
              <div className="mt-4 grid gap-2">
                {group.posts.map((post) => (
                  <Link
                    key={post.slug}
                    href={`/blog/${post.slug}`}
                    className="group rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:border-orange-300"
                  >
                    <span className="block text-sm font-semibold leading-snug text-gray-950 group-hover:text-orange-700">
                      {post.title}
                    </span>
                    <span className="mt-1 line-clamp-2 block text-xs leading-5 text-gray-500">
                      {post.description}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestStoryVisual() {
  return (
    <div className="mt-8 flex justify-center" aria-hidden="true">
      <div className="relative h-36 w-56">
        <div className="absolute left-0 top-5 h-24 w-36 rotate-[-5deg] rounded-lg border border-orange-200 bg-white shadow-sm">
          <div className="flex h-full flex-col justify-between p-4">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-orange-500" />
              <span className="h-2 w-16 rounded-full bg-orange-200" />
            </div>
            <div className="space-y-2">
              <span className="block h-2 w-full rounded-full bg-gray-200" />
              <span className="block h-2 w-2/3 rounded-full bg-gray-200" />
            </div>
          </div>
        </div>
        <div className="absolute right-0 top-0 grid h-24 w-24 place-items-center rounded-full bg-orange-500 text-white shadow-md">
          <Mic2 className="h-9 w-9" />
        </div>
        <div className="absolute bottom-0 right-7 flex h-16 w-32 items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-4 text-emerald-800 shadow-sm">
          <MessageCircle className="h-5 w-5" />
          <span className="text-xs font-bold uppercase tracking-[0.16em]">Speak</span>
        </div>
      </div>
    </div>
  );
}

export default function BlogPageClient({ blogPosts }: BlogPageClientProps) {
  const [filterType, setFilterType] = useState<FilterType>("category");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const sortedPosts = useMemo(
    () => [...blogPosts].sort((a, b) => b.date.localeCompare(a.date)),
    [blogPosts]
  );

  const categories = useMemo(
    () => ["all", ...Array.from(new Set(sortedPosts.map((post) => post.category)))],
    [sortedPosts]
  );
  const languages = useMemo(
    () => ["all", ...Array.from(new Set(sortedPosts.map((post) => resolveBlogPostLanguage(post))))],
    [sortedPosts]
  );

  const categoryArchives = categories.filter((category) => category !== "all");
  const languageArchives = languages.filter((language) => language !== "all");
  const filterValues = filterType === "category" ? categories : languages;

  const filteredPosts = useMemo(() => {
    const normalizedSearch = normalizeSearchValue(searchQuery);

    return sortedPosts.filter((post) => {
      const displayLanguage = resolveBlogPostLanguage(post);
      const matchesFilter =
        activeFilter === "all" ||
        (filterType === "category"
          ? post.category === activeFilter
          : displayLanguage === activeFilter);

      if (!matchesFilter) return false;
      if (!normalizedSearch) return true;

      return [post.title, post.description, post.category, displayLanguage]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [activeFilter, filterType, searchQuery, sortedPosts]);

  const featuredPost = filteredPosts[0] ?? sortedPosts[0];
  const articlePosts = filteredPosts.slice(featuredPost ? 1 : 0);

  return (
    <main className="min-h-screen bg-[#fbfaf7] text-gray-950">
      <section className="border-b border-orange-100 bg-[#fff9f1]">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-12 sm:px-6 lg:grid-cols-[1fr_390px] lg:px-8 lg:py-16">
          <div className="flex flex-col justify-center">
            <div className="flex w-fit items-center gap-2 rounded-full border border-orange-200 bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-orange-700">
              <Sparkles className="h-3.5 w-3.5" />
              ChickyTutor Journal
            </div>
            <h1 className="mt-5 max-w-3xl font-serif text-5xl font-semibold leading-[0.98] tracking-tight text-gray-950 sm:text-6xl lg:text-7xl">
              Useful language advice for people who want to speak.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">
              Practical guides, app comparisons, study routines, and product notes for learners who
              want conversation practice to feel less intimidating.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                href="/"
                className="inline-flex min-h-11 items-center gap-2 rounded-full bg-orange-500 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600"
              >
                <Mic2 className="h-4 w-4" />
                Start speaking
              </Link>
              <a
                href="#latest-guides"
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-gray-300 bg-white px-5 text-sm font-semibold text-gray-800 transition-colors hover:border-orange-300 hover:text-orange-700"
              >
                <BookOpen className="h-4 w-4" />
                Browse guides
              </a>
            </div>
          </div>

          <div className="relative min-h-[340px] overflow-hidden rounded-lg border border-orange-200 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-br from-orange-100 via-amber-50 to-white" />
            <div className="relative p-6">
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-700">
                  Latest from the blog
                </p>
                <span className="rounded-full bg-gray-950 px-3 py-1 text-xs font-semibold text-white">
                  {sortedPosts.length} posts
                </span>
              </div>
              <LatestStoryVisual />
              {sortedPosts[0] ? (
                <Link href={`/blog/${sortedPosts[0].slug}`} className="mt-8 block">
                  <p className="text-sm font-semibold text-orange-700">
                    {toCategoryLabel(sortedPosts[0].category)}
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold leading-tight text-gray-950">
                    {sortedPosts[0].title}
                  </h2>
                  <p className="mt-3 line-clamp-2 text-sm leading-6 text-gray-600">
                    {sortedPosts[0].description}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-orange-700">
                    Read latest
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-400">Articles</p>
            <p className="mt-1 text-3xl font-semibold text-gray-950">{sortedPosts.length}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-400">Topics</p>
            <p className="mt-1 text-3xl font-semibold text-gray-950">{categoryArchives.length}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-400">Languages</p>
            <p className="mt-1 text-3xl font-semibold text-gray-950">{languageArchives.length}</p>
          </div>
        </div>

        <section className="mt-8 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <FilterButton
                active={filterType === "category"}
                onClick={() => {
                  setFilterType("category");
                  setActiveFilter("all");
                }}
              >
                Categories
              </FilterButton>
              <FilterButton
                active={filterType === "language"}
                onClick={() => {
                  setFilterType("language");
                  setActiveFilter("all");
                }}
              >
                <Languages className="mr-2 h-4 w-4" />
                Languages
              </FilterButton>
            </div>
            <label className="relative block w-full lg:max-w-sm">
              <span className="sr-only">Search blog posts</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search guides, apps, languages..."
                className="h-11 w-full rounded-full border border-gray-200 bg-[#fbfaf7] pl-10 pr-4 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-orange-300 focus:bg-white focus:ring-2 focus:ring-orange-100"
              />
            </label>
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {filterValues.map((value) => {
              const label =
                value === "all"
                  ? "All"
                  : filterType === "category"
                    ? toCategoryLabel(value)
                    : value;

              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setActiveFilter(value)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeFilter === value
                      ? "border-orange-500 bg-orange-50 text-orange-700"
                      : "border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-700"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </section>
      </section>

      <SeoGrowthLinks posts={sortedPosts} />
      <HandwrittenClusterLinks posts={sortedPosts} />

      <section id="latest-guides" className="mx-auto max-w-7xl px-5 pb-16 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
                  Latest guides
                </p>
                <h2 className="mt-2 font-serif text-4xl font-semibold tracking-tight text-gray-950">
                  Start with the useful stuff.
                </h2>
              </div>
              <p className="text-sm font-medium text-gray-500">
                Showing {filteredPosts.length} of {sortedPosts.length}
              </p>
            </div>

            <div className="mt-6">
              {featuredPost ? <FeaturedArticle post={featuredPost} /> : null}
            </div>

            {articlePosts.length > 0 ? (
              <motion.div
                className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.04,
                    },
                  },
                }}
              >
                <AnimatePresence>
                  {articlePosts.map((post, index) => (
                    <ArticleCard
                      key={`${filterType}-${activeFilter}-${post.slug}`}
                      post={post}
                      index={index}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>
            ) : filteredPosts.length === 0 ? (
              <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
                <p className="text-lg font-semibold text-gray-950">No guides match that search.</p>
                <p className="mt-2 text-sm text-gray-500">
                  Try a broader language, topic, or app name.
                </p>
              </div>
            ) : null}
          </div>

          <div className="space-y-5">
            <PracticeRail />
            <ArchiveLinks categories={categoryArchives} languages={languageArchives} />
          </div>
        </div>
      </section>
    </main>
  );
}
